/**
 * MyWorkStoppagesView
 *
 * A collab's own arrêts de travail — what "Work stoppages" shows to anyone without the
 * `workStoppages` scope. Basics only (type, period, status, justificatifs): the CPAM and
 * prévoyance figures, the maintien split and Paie's notes are HR's working data, and the API
 * never sends them to this screen.
 *
 * A request can be changed or cancelled while it is pending. Once HR has validated or
 * refused it, it is read-only — here, and on the server, which is what actually enforces it.
 */
import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import moment from "moment";
import {
  Box, Button, Chip, IconButton, Paper, Stack, Table, TableBody, TableCell, TableContainer,
  TableHead, TableRow, Tooltip, Typography,
  Dialog, DialogTitle, DialogContent, DialogContentText, DialogActions,
} from "@mui/material";
import AddCircleOutlineIcon from "@mui/icons-material/AddCircleOutline";
import VisibilityRoundedIcon from "@mui/icons-material/VisibilityRounded";
import EditRoundedIcon from "@mui/icons-material/EditRounded";
import DeleteOutlineRoundedIcon from "@mui/icons-material/DeleteOutlineRounded";
import LockRoundedIcon from "@mui/icons-material/LockRounded";
import AttachFileRoundedIcon from "@mui/icons-material/AttachFileRounded";
import Langs, { Localize } from "../../app/lang/langs";
import { showErrorToast, showSuccessToast } from "../../app/utils/helpers";
import { getMyWorkStoppages, cancelMyWorkStoppage } from "../../components/workStoppages/workStoppagesSlice";
import MyWorkStoppageFormModal, { myWorkStoppageError } from "./MyWorkStoppageFormModal";
import { leavePeriodFromTimestamps, periodLabelKey } from "../../components/inputs/LeaveDateRangeField";

/** The planning leave's status, in MUI's intents. */
const LEAVE_STATUS_COLORS = {
  PENDING: "warning",
  VALIDATED: "success",
  REJECTED: "error",
};

/** The planning stores a period in epoch ms. */
const formatDate = (value) => (value ? moment(Number(value)).format("DD/MM/YYYY") : "—");

/** A bound of the period with its half day, as Congés shows it: "12/09/2026 · Matin". */
const formatBound = (row, edge) => {
  if (!row.from || !row.to) return "—";
  const period = leavePeriodFromTimestamps(row.from, row.to);
  return edge === "start"
    ? `${formatDate(row.from)} · ${Localize(periodLabelKey(period.start_period))}`
    : `${formatDate(row.to)} · ${Localize(periodLabelKey(period.end_period))}`;
};

export default function MyWorkStoppagesView() {
  const dispatch = useDispatch();
  const isMobile = useSelector((state) => state.settings.isMobile);
  const list = useSelector((state) => state.workStoppages.myList);
  const loading = useSelector((state) => state.workStoppages.loadingMyList);

  const [formOpen, setFormOpen] = useState(false);
  // The row the form is open on; null with the form open means "declare a new one".
  const [selected, setSelected] = useState(null);
  const [toCancel, setToCancel] = useState(null);
  const [cancelling, setCancelling] = useState(false);

  const isPhone = isMobile === 2;

  useEffect(() => {
    dispatch(getMyWorkStoppages());
  }, [dispatch]);

  const refresh = () => dispatch(getMyWorkStoppages());

  const openForm = (row) => {
    setSelected(row);
    setFormOpen(true);
  };

  async function confirmCancel() {
    // Guarded so a double click cannot send the cancel twice.
    if (!toCancel || cancelling) return;
    setCancelling(true);
    try {
      const response = await dispatch(cancelMyWorkStoppage({ id: toCancel.id }));
      const data = response?.payload?.responseData;
      if (data && data.status === 1) {
        showSuccessToast(Localize("MY_WORK_STOPPAGE_CANCELLED"));
      } else {
        showErrorToast(myWorkStoppageError(data));
      }
      refresh();
    } finally {
      setCancelling(false);
      setToCancel(null);
    }
  }

  return (
    <Box sx={{ height: "100%", width: "100%" }}>
      <Box
        className="topMenu"
        sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", pr: isPhone ? 1 : 3 }}
      >
        <span className="headerTitle" style={{ fontWeight: 700, letterSpacing: "-0.02em" }}>
          <Langs str="WORK_STOPPAGES" />
        </span>
        <Button
          variant="contained"
          color="warning"
          size="small"
          startIcon={<AddCircleOutlineIcon />}
          onClick={() => openForm(null)}
          sx={{ textTransform: "none", fontWeight: 600, borderRadius: "10px" }}
        >
          <Langs str="WORK_STOPPAGE_ADD" />
        </Button>
      </Box>

      {/* The top bar is `position: fixed`, so the content starts below it. */}
      <Box sx={{ px: isPhone ? 2 : 3, pb: 2, pt: isPhone ? "110px" : (isMobile === 3 ? "95px" : "75px") }}>
        <TableContainer component={Paper} variant="outlined" sx={{ borderRadius: "12px", overflowX: "auto" }}>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>{Localize("Type")}</TableCell>
                <TableCell>{Localize("START_DATE")}</TableCell>
                <TableCell>{Localize("END_DATE")}</TableCell>
                {!isPhone && <TableCell>{Localize("Duration")}</TableCell>}
                <TableCell>{Localize("Status")}</TableCell>
                {!isPhone && <TableCell align="center"><AttachFileRoundedIcon fontSize="small" /></TableCell>}
                <TableCell align="right" />
              </TableRow>
            </TableHead>
            <TableBody>
              {(list || []).map((row) => (
                <TableRow key={row.id} hover sx={{ cursor: "pointer" }} onClick={() => openForm(row)}>
                  <TableCell>{[row.type, row.leave_label].filter(Boolean).join(" - ")}</TableCell>
                  <TableCell>{formatBound(row, "start")}</TableCell>
                  <TableCell>{formatBound(row, "end")}</TableCell>
                  {!isPhone && <TableCell>{row.duration ?? "—"}</TableCell>}
                  <TableCell>
                    <Chip
                      size="small"
                      variant="outlined"
                      color={LEAVE_STATUS_COLORS[row.status] || "default"}
                      label={Localize(`WORK_STOPPAGE_LEAVE_STATUS_${row.status}`)}
                      sx={{ fontWeight: 600, borderRadius: "8px" }}
                    />
                  </TableCell>
                  {!isPhone && <TableCell align="center">{Number(row.attachments_count) || "—"}</TableCell>}
                  {/* Stops the row's own click, so an action never also opens the form. */}
                  <TableCell align="right" onClick={(e) => e.stopPropagation()}>
                    {row.locked ? (
                      <Stack direction="row" spacing={0.25} justifyContent="flex-end" alignItems="center">
                        <Tooltip title={Localize("MY_WORK_STOPPAGE_LOCKED")}>
                          <LockRoundedIcon fontSize="small" sx={{ color: "text.disabled", mr: 0.5 }} />
                        </Tooltip>
                        <Tooltip title={Localize("VIEW_DETAILS")}>
                          <IconButton size="small" onClick={() => openForm(row)}>
                            <VisibilityRoundedIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      </Stack>
                    ) : (
                      <Stack direction="row" spacing={0.25} justifyContent="flex-end">
                        <Tooltip title={Localize("MY_WORK_STOPPAGE_EDIT_TITLE")}>
                          <IconButton size="small" onClick={() => openForm(row)}>
                            <EditRoundedIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title={Localize("MY_WORK_STOPPAGE_CANCEL")}>
                          <IconButton size="small" color="error" onClick={() => setToCancel(row)}>
                            <DeleteOutlineRoundedIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      </Stack>
                    )}
                  </TableCell>
                </TableRow>
              ))}
              {!loading && !(list || []).length && (
                <TableRow>
                  <TableCell colSpan={isPhone ? 5 : 7}>
                    <Typography variant="body2" color="text.secondary" sx={{ py: 2, textAlign: "center", opacity: 0.7 }}>
                      <Langs str="MY_WORK_STOPPAGES_EMPTY" />
                    </Typography>
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </Box>

      <MyWorkStoppageFormModal
        visible={formOpen}
        leave={selected}
        onHide={() => { setFormOpen(false); setSelected(null); }}
        onSaved={refresh}
      />

      <Dialog
        open={!!toCancel}
        onClose={() => !cancelling && setToCancel(null)}
        fullWidth
        maxWidth="xs"
      >
        <DialogTitle sx={{ fontWeight: 700 }}>
          <Langs str="MY_WORK_STOPPAGE_CANCEL" />
        </DialogTitle>
        <DialogContent>
          <DialogContentText>
            <Langs str="MY_WORK_STOPPAGE_CANCEL_MSG" />
          </DialogContentText>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button color="inherit" onClick={() => setToCancel(null)} disabled={cancelling}>
            <Langs str="MY_WORK_STOPPAGE_KEEP" />
          </Button>
          <Button variant="contained" color="error" onClick={confirmCancel} disabled={cancelling}>
            <Langs str="MY_WORK_STOPPAGE_CANCEL" />
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
